import { ACCESS_TOKEN, REFRESH_TOKEN } from '@shared/const';
import { iconsPath } from '@shared/css.const';
import Cookies from 'js-cookie';

export { convertToDDMMYYYYFormat, formatToLocalTimeZone } from '@shared/utils/date-utils';

export const debounce = (callback: (...args: any[]) => void, wait: number) => {
  let timeoutId: number | null = null;
  return (...args: any[]) => {
    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }
    // eslint-disable-next-line no-return-assign
    return timeoutId = window.setTimeout(() => callback.apply(this, args), wait);
  };
};

export const getDate = (date: Date | null | undefined) => {
  if (!date || !(date instanceof Date)) {
    throw new Error('Invalid date format');
  }
  return date.toISOString()?.split('T')?.[0];
};

export const covertToYYYYMMDDFormat = (date: string | Date) => {
  const isoDate = typeof date === 'object' ? date : new Date(date);
  const year = isoDate.getFullYear();
  const month = String(isoDate.getMonth() + 1).padStart(2, '0'); // Month is zero-based
  const day = String(isoDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const convertToDDMMFormat = (date: string | Date) => {
  const isoDate = date instanceof Date ? date : new Date(date);
  const day = String(isoDate.getDate()).padStart(2, '0');
  const month = String(isoDate.getMonth() + 1).padStart(2, '0');

  return `${day}/${month}`;
};

export const removeAuthCookies = (isSubdomain?: boolean) => {
  if (isSubdomain) {
    Cookies.remove(ACCESS_TOKEN, { domain: COOKIE_SUBDOMAIN });
    Cookies.remove(REFRESH_TOKEN, { domain: COOKIE_SUBDOMAIN });
  }

  Cookies.remove(ACCESS_TOKEN);
  Cookies.remove(REFRESH_TOKEN);
};

export const countDaysFromDate = (date: Date) => {
  const currentDate = new Date();
  const timeDiff = currentDate.getTime() - date.getTime();
  const aDayInMilliseconds = 1000 * 3600 * 24;
  return Math.floor(timeDiff / aDayInMilliseconds);
};

export const getDocumentsExtensionIcon = (fileName: string) => {
  if (fileName?.endsWith('.jpg')) {
    return `${iconsPath}/jpg-icon.svg`;
  }
  if (fileName?.endsWith('.png')) {
    return `${iconsPath}/png-icon.svg`;
  }
  return `${iconsPath}/pdf-icon.svg`;
};

export const calculateYearsAndMonthsFromMonths = (months: number | undefined): {
  years: number | null;
  months: number | null;
} => {
  // Check if 'months' is undefined, null, or less than 0
  if (months == null || months < 0) {
    return {
      years: null,
      months: null,
    };
  }

  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  return {
    years,
    months: remainingMonths,
  };
};

export const formatPercentage = (value: number | string | null | undefined) =>
  (typeof value === 'string' ? Number.parseFloat(value) : value);

export const formatToHoursMinutes = (d: Date) => {
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

export const snakeCaseToTitle = (text: string): string => {
  if (!text || typeof text !== 'string') return '';
  return text.replace(/^_*(.)/, (_, c) => c.toUpperCase())
    .replaceAll(/_+(.)/g, (_, c) => ` ${c.toUpperCase()}`);
};

export const formatNumber = (number: number | undefined, showFloat: boolean = true): string => {
  if (!number) return '0';

  const userLocale = navigator.language || 'en-US';
  const digits = showFloat ? 2 : 0;
  const options = { minimumFractionDigits: digits, maximumFractionDigits: digits };

  return new Intl.NumberFormat(userLocale, options).format(number);
};

export const shareToEmail = (emailSubject: string, shareMessageContent: string, email: string = '') => {
  window.open(`mailto:${email}?subject=${emailSubject}&body=${shareMessageContent}`, '_blank');
};
export const shareToWhatsapp = (shareMessageContent: string) => {
  const encodedText = encodeURIComponent(shareMessageContent);
  window.open(`https://wa.me/?text=${encodedText}`, '_blank');
};

export const scrollToError = (error: Record<string, any>) => {
  const errorKeys = Object.keys(error);
  document
    .querySelector(`[id="${errorKeys?.[0]}"]`)
    ?.scrollIntoView({
      block: 'center',
      behavior: 'smooth',
      inline: 'center',
    });
};

export const scrollToLayoutV2ElementWithOffset = (targetElement: string, offset = 0) => {
  const element = document.querySelector((`[id="${targetElement}"]`));
  const container = document.querySelector('[id="layout-v2-scrollable"]')?.firstElementChild;

  if (!element || !container) {
    return;
  }

  const elementPosition = element.getBoundingClientRect().top + window.scrollY;
  const offsetPosition = elementPosition - offset;
  container.scrollTo({
    top: offsetPosition,
    behavior: 'smooth',
  });
};

export const objectKeysToSnakePascalCase = (obj: Record<string, any>): Record<string, any> => {
  if (typeof obj !== 'object' || obj === null) return obj;

  return Object.keys(obj).reduce((acc: Record<string, any>, key: string) => {
    const pascalKey = key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('_');

    return {
      ...acc,
      [pascalKey]: typeof obj[key] === 'object' && !Array.isArray(obj[key])
        ? objectKeysToSnakePascalCase(obj[key])
        : obj[key],
    };
  }, {});
};

export const returnIfSame = <T>(
  arr1: T[],
  arr2: T[]
): T[] => {
  const set1 = new Set(arr1);
  const set2 = new Set(arr2);
  const set3 = new Set([...arr1, ...arr2]);

  if (set1.size === set2.size && set2.size === set3.size) {
    return arr1;
  }
  return [];
};

export const timeUntilHHMM = (futureDate: string): string => {
  const targetDate = new Date(futureDate);
  const now = new Date();

  // Calculate the difference in milliseconds
  const diffInMs = targetDate.getTime() - now.getTime();

  // If the date is in the past, return '0h 0m'
  if (diffInMs <= 0) {
    return '0h 0m';
  }

  // Convert milliseconds to hours and minutes
  const hours = Math.floor(diffInMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours === 0) return `${minutes} m`;

  return `${hours}h ${minutes}m`;
};
